import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

type Rating = "green" | "yellow" | "red";

interface Context {
  customerName: string | null;
  vehicle: string | null;
  dealershipName: string | null;
  repName: string | null;
}

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-csi-feedback`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const RATING_LABEL: Record<Rating, { label: string; emoji: string; color: string }> = {
  green: { label: "Great", emoji: "😀", color: "bg-green-600 hover:bg-green-700" },
  yellow: { label: "Okay", emoji: "😐", color: "bg-amber-500 hover:bg-amber-600" },
  red: { label: "Not great", emoji: "😞", color: "bg-red-600 hover:bg-red-700" },
};

export default function CsiSubmit() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const presetRating = (params.get("rating") as Rating | null) ?? null;

  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState<string | null>(null);
  const [ctx, setCtx] = useState<Context | null>(null);
  const [rating, setRating] = useState<Rating | null>(presetRating);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrorState("Missing link token.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${FUNCTIONS_URL}?token=${encodeURIComponent(token)}`, {
          headers: { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
        });
        const body = await res.json();
        if (!res.ok || !body?.ok) {
          if (body?.error === "already_used") setErrorState("This feedback link has already been used. Thanks!");
          else if (body?.error === "expired") setErrorState("This feedback link has expired.");
          else if (body?.error === "invalid_token") setErrorState("This feedback link is no longer valid.");
          else setErrorState("Sorry — we couldn't load this feedback page.");
        } else {
          setCtx({
            customerName: body.customerName,
            vehicle: body.vehicle,
            dealershipName: body.dealershipName,
            repName: body.repName,
          });
        }
      } catch {
        setErrorState("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!rating || !token) return;
    setSubmitting(true);
    try {
      const res = await fetch(FUNCTIONS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
        },
        body: JSON.stringify({ token, rating, notes: notes.trim() || undefined }),
      });
      const body = await res.json();
      if (!res.ok || !body?.ok) {
        setErrorState(body?.error === "already_used" ? "Already submitted — thanks!" : "Couldn't submit. Try again.");
      } else {
        setDone(true);
      }
    } catch {
      setErrorState("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-start justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-xs font-extrabold tracking-[0.18em] text-primary mb-1">PULL OVER SALES</div>
        {ctx?.dealershipName && (
          <div className="text-sm text-muted-foreground mb-8">{ctx.dealershipName}</div>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {!loading && errorState && (
          <div className="rounded-lg border border-border bg-card p-6">
            <h1 className="text-lg font-semibold mb-2">{errorState}</h1>
            <p className="text-sm text-muted-foreground">
              You can close this window. If you think this is a mistake, contact the dealership directly.
            </p>
          </div>
        )}

        {!loading && !errorState && done && (
          <div className="rounded-lg border border-border bg-card p-6 text-center">
            <div className="text-4xl mb-3">🙏</div>
            <h1 className="text-xl font-semibold mb-2">Thanks for the feedback!</h1>
            <p className="text-sm text-muted-foreground">
              {ctx?.dealershipName ? `${ctx.dealershipName} appreciates it.` : "Your dealership appreciates it."}
            </p>
          </div>
        )}

        {!loading && !errorState && !done && ctx && (
          <>
            <h1 className="text-2xl font-bold tracking-tight mb-2">
              How did we do{ctx.customerName ? `, ${ctx.customerName.split(" ")[0]}` : ""}?
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {ctx.vehicle ? <>About <strong className="text-foreground">{ctx.vehicle}</strong></> : "About your recent visit"}
              {ctx.repName ? <> with <strong className="text-foreground">{ctx.repName}</strong></> : null}.
              One tap is enough.
            </p>

            <div className="grid grid-cols-3 gap-3 mb-6">
              {(Object.keys(RATING_LABEL) as Rating[]).map((r) => {
                const meta = RATING_LABEL[r];
                const active = rating === r;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRating(r)}
                    className={`flex flex-col items-center justify-center rounded-lg border py-4 text-sm font-semibold transition ${
                      active
                        ? `${meta.color} text-white border-transparent`
                        : "bg-card border-border hover:border-foreground/30 text-foreground"
                    }`}
                  >
                    <span className="text-2xl mb-1">{meta.emoji}</span>
                    {meta.label}
                  </button>
                );
              })}
            </div>

            <label className="block text-xs font-medium text-muted-foreground mb-2">
              Anything you'd like to share? (optional)
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What went well — or what didn't?"
              rows={4}
              className="mb-5"
            />

            <Button
              onClick={submit}
              disabled={!rating || submitting}
              className="w-full"
              size="lg"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit feedback
            </Button>

            <p className="text-xs text-muted-foreground mt-6 text-center">
              Powered by Pull Over Sales · Your response goes only to {ctx.dealershipName ?? "the dealership"}.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
